import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import { ToggleOption, UiButtonComponent, UIIconComponent, UiToggleButtonComponent } from '@design-system';
import { getSortDirectionLabels, PORTFOLIO_SORT_CRITERION_LABELS } from '../../../../models/portfolio-sort.utils';
import {
	PORTFOLIO_SORT_CRITERIA,
	PortfolioSortCriterion,
	PortfolioSortDirection
} from '../../../../models/user-setting.model';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';

/** What one criterion row renders. */
interface SortCriterionRow {
	criterion: PortfolioSortCriterion;
	label: string;
	selected: boolean;
}

/**
 * The holdings list's sort sheet, opened from the column header above the cards.
 *
 * Distinct from the three-dot settings sheet, per the handoff: this one is
 * reached only by tapping the sort box, and carries only the sort.
 *
 * It has no save button, and deliberately: the sheet is a choice of one
 * criterion and one direction, so both apply the moment they are tapped and
 * {@link sortChanged} carries them out. Choosing a criterion also closes the
 * sheet — it is the trader's last decision here — while the direction toggle
 * leaves it open, so flipping the direction and then picking a column is one
 * visit rather than two.
 */
@Component({
	selector: 'app-portfolio-sort-bottom-sheet',
	imports: [BottomSheetComponent, UiButtonComponent, UiToggleButtonComponent, UIIconComponent],
	templateUrl: './portfolio-sort-bottom-sheet.component.html',
	host: {
		'data-cy': 'portfolio-sort-bottom-sheet'
	}
})
export class PortfolioSortBottomSheetComponent {
	/** The sort in force, as the stored setting has it. */
	readonly criterion = input.required<PortfolioSortCriterion>();
	readonly direction = input.required<PortfolioSortDirection>();

	readonly sortChanged = output<{ criterion: PortfolioSortCriterion; direction: PortfolioSortDirection }>();

	private readonly sheet = viewChild<BottomSheetComponent>('sheet');

	/**
	 * The selection as the sheet shows it.
	 *
	 * Held locally rather than read straight off the inputs so a tap lands on
	 * screen immediately: the stored setting only catches up once the write to
	 * `/userSettings` returns, and a radio row that waits on the network to check
	 * itself reads as a dropped tap. Re-seeded from the inputs by {@link open},
	 * so a failed write is corrected the next time the sheet is opened rather
	 * than persisting a choice the API rejected.
	 */
	private readonly selectedCriterion = signal<PortfolioSortCriterion>('netSaleValue');
	private readonly selectedDirection = signal<PortfolioSortDirection>('desc');

	readonly criterionRows = computed<SortCriterionRow[]>(() =>
		PORTFOLIO_SORT_CRITERIA.map(criterion => ({
			criterion,
			label: PORTFOLIO_SORT_CRITERION_LABELS[criterion],
			selected: criterion === this.selectedCriterion()
		}))
	);

	readonly activeDirection = this.selectedDirection.asReadonly();

	/**
	 * The toggle's two options. Descending sits first so it renders on the
	 * right in this RTL layout, matching the handoff: the group is a plain
	 * `inline-flex` row with no reversal, so the first option is the one on the
	 * trailing — in RTL, right-hand — edge. The wording follows the criterion,
	 * ی تا الف for the symbol name and زیاد به کم for a figure.
	 */
	readonly directionOptions = computed<ToggleOption[]>(() => {
		const labels = getSortDirectionLabels(this.selectedCriterion());

		return [
			{ label: labels.desc, value: 'desc', icon: 'sort-up' },
			{ label: labels.asc, value: 'asc', icon: 'sort-down' }
		];
	});

	/** Seed the sheet from the stored sort, then present it. */
	open(): Promise<void> {
		this.selectedCriterion.set(this.criterion());
		this.selectedDirection.set(this.direction());

		return this.sheet()?.open() ?? Promise.resolve();
	}

	close(): Promise<void> {
		return this.sheet()?.close() ?? Promise.resolve();
	}

	/** Scenario 1: the cards reorder on the criterion the trader picked. */
	async onCriterionClick(criterion: PortfolioSortCriterion): Promise<void> {
		this.selectedCriterion.set(criterion);
		this.emitSort();
		await this.close();
	}

	/** Scenario 2: the same criterion, run the other way. */
	onDirectionChange(direction: string | number): void {
		if (direction !== 'asc' && direction !== 'desc') {
			return;
		}

		this.selectedDirection.set(direction);
		this.emitSort();
	}

	private emitSort(): void {
		this.sortChanged.emit({ criterion: this.selectedCriterion(), direction: this.selectedDirection() });
	}
}
